import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

const GetAppPage = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
  }, []);

  return (
    <main
      id="download"
      className="pt-16 md:pt-20"
      style={{ scrollMarginTop: '80px', fontFamily: "'Open Sans', sans-serif" }}
      data-testid="get-app-page"
    >
      <Helmet>
        <title>Join free — Servitium</title>
        <meta name="description" content="Run your business on a system, on any device. Free across the Lower Mainland. Available on iOS and Android." />
        <meta property="og:title" content="Join free — Servitium" />
        <meta property="og:description" content="Run your business on a system, on any device. Free across the Lower Mainland. Available on iOS and Android." />
        <meta property="og:url" content="https://servitium.ca/get" />
        <meta name="twitter:title" content="Join free — Servitium" />
        <meta name="twitter:description" content="Run your business on a system, on any device. Free across the Lower Mainland. Available on iOS and Android." />
        <meta property="og:image" content="https://servitium.ca/og-preview.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Servitium — Infrastructure for solo and micro-businesses" />
        <meta name="twitter:image" content="https://servitium.ca/og-preview.png" />
      </Helmet>
      {/* Inject animations */}
      <style>{`
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          14% { transform: scale(1.04); }
          28% { transform: scale(1); }
          42% { transform: scale(1.04); }
          56% { transform: scale(1); }
        }
      `}</style>

      <section
        className="relative py-20 md:py-28 lg:py-32 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #880808 0%, #5e0505 100%)' }}
        data-testid="download-section"
      >
        {/* Radial glow behind phone area */}
        <div className="absolute top-1/2 right-[25%] -translate-y-1/2 w-[600px] h-[600px] bg-white/[0.06] rounded-full blur-[120px] pointer-events-none hidden lg:block" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">

            {/* ── LEFT COLUMN ── */}
            <div>
              {/* Eyebrow */}
              <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-white/60 mb-6" data-testid="download-eyebrow">
                FREE ACROSS THE LOWER MAINLAND
              </p>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[1.08] mb-8" data-testid="download-headline">
                <span className="text-white">
                  RUN YOUR{' '}
                  <span style={{ display: 'inline-block', animation: 'heartbeat 2.5s ease-in-out infinite' }}>BUSINESS</span>
                </span>
                <br />
                <span style={{ color: '#D4AF37' }}>ON A SYSTEM,</span>
                <br />
                <span className="text-white">ON ANY DEVICE.</span>
              </h1>

              {/* Body paragraph */}
              <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed mb-2 max-w-lg">
                Verified identity, structured job workflow, in-app payments, and a track record that follows you. Everything in one app.
              </p>
              <p
                className="text-3xl md:text-4xl font-extrabold text-white uppercase tracking-tight mb-8"
                style={{ animation: 'heartbeat 2.5s ease-in-out infinite 0.3s' }}
              >
                ALWAYS.
              </p>

              {/* Secondary line */}
              <p className="text-sm md:text-base text-white/60 mb-6">
                Choose your device to install.
              </p>

              {/* Store buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                {/* Apple App Store */}
                <a
                  href="https://apps.apple.com/ca/app/servitium/id6754441132"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform duration-300 hover:scale-105"
                  data-testid="app-store-button"
                >
                  <img src="/badge-appstore.svg" alt="Download on the App Store" className="h-[52px] w-auto" style={{ borderRadius: 0 }} />
                </a>

                {/* Google Play */}
                <a
                  href="https://play.google.com/store/apps/details?id=com.theprojectkiss.servitium&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform duration-300 hover:scale-105"
                  data-testid="play-store-button"
                >
                  <img src="/badge-googleplay.svg" alt="Get it on Google Play" className="h-[52px] w-auto" style={{ borderRadius: 0 }} />
                </a>
              </div>

              {/* Microcopy */}
              <p className="text-sm italic text-white/60 mb-8">
                Every job adds to your record.
              </p>

              {/* QR block */}
              <div className="inline-flex items-center gap-4 bg-white/10 border border-white/20 backdrop-blur-md rounded-full px-5 py-3 mb-8" data-testid="qr-block">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-1">
                  <img src="/qr-code.png" alt="QR Code" className="w-full h-full object-contain" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white leading-tight">On Desktop? Scan to install</p>
                  <p className="text-xs text-white/60">Available on iOS &amp; Android</p>
                </div>
              </div>

              {/* Bottom links */}
              <div>
                <p className="text-sm text-white/70 mb-6">
                  Not ready to download?{' '}
                  <a
                    href="https://servitium.info/talk-to-the-team"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-white transition-colors"
                    data-testid="talk-to-team-link"
                  >
                    Talk to our team
                  </a>
                </p>

                <div className="border-t border-white/20 pt-5">
                  <p className="text-xs text-white/50 leading-relaxed">
                    Available across the Lower Mainland. Expanding across BC and the rest of Canada soon.
                  </p>
                </div>
              </div>
            </div>

            {/* ── RIGHT COLUMN: PHONE MOCKUP ── */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                {/* Background glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-white/[0.05] rounded-full blur-[80px] pointer-events-none" />

                {/* Phone frame */}
                <div
                  className="relative w-[280px] md:w-[300px] rounded-[3rem] p-[6px] shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
                  style={{ background: 'linear-gradient(145deg, #2a2a2a 0%, #111 50%, #2a2a2a 100%)' }}
                  data-testid="phone-mockup"
                >
                  {/* Inner ring highlight */}
                  <div className="rounded-[2.7rem] p-[2px]" style={{ background: 'linear-gradient(145deg, rgba(255,255,255,0.15), rgba(255,255,255,0.02))' }}>
                    {/* Screen area */}
                    <div className="relative bg-black rounded-[2.6rem] overflow-hidden" style={{ aspectRatio: '9/19.5' }}>
                      {/* Video */}
                      <video
                        ref={videoRef}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="absolute inset-0 w-full h-full object-cover"
                        src="/app-demo.mp4"
                        data-testid="phone-video"
                      />

                      {/* Home indicator */}
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[100px] h-[4px] bg-white/30 rounded-full z-20" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default GetAppPage;
